
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Database, DatabaseReference, equalTo, listVal, objectVal, orderByChild, query, ref, update } from '@angular/fire/database';
import { getToken, Messaging, onMessage } from '@angular/fire/messaging';
import { catchError, Observable, pipe, Subscription, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  NEW_REQUEST_SUBSCRIPTION: Subscription = new Subscription();
  DATABASE_PATH: string = 'CarFinderGeniePlatform/notifications/';

  // Detección de plataforma
  private isSafari: boolean = false;
  private isIOS: boolean = false;
  private isNotificationSupported: boolean = false;
  private sentNotifications: Set<string> = new Set();
  private readonly NOTIFICATION_TTL = 5000; // 5 segundos

  constructor(
              private messaging: Messaging,
              private DATABASE: Database,
              private httpClient: HttpClient,
              ) {
    this.detectPlatform();
  }

  private detectPlatform(): void {
    // Detectar iOS
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Verificar soporte de Notification API
    this.isNotificationSupported = 'Notification' in window &&
                                  typeof Notification.requestPermission === 'function';

    // // // console.log('🔔 Notification Platform Detection:', {
    //   isIOS: this.isIOS,
    //   isNotificationSupported: this.isNotificationSupported,
    //   userAgent: navigator.userAgent
    // });
  }

  public requestiOSNotificationPermission(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      await Notification.requestPermission( async (permission: NotificationPermission) => {
        if (permission == 'granted') {
          await this.getNotificationToken().then((token: string) => {
            resolve(token);
          });
        }
      }).catch((error: any) => {
        reject(error)
      })
    });
  }

  public getCurrentNotificationPermission(): NotificationPermission {
    return Notification.permission;
  }

  public async requestNotificationPermissions(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        // Verificar si las notificaciones están soportadas
        if (!this.isNotificationSupported) {
          // // console.warn('🚫 Notification API no soportada en esta plataforma');
          if (this.isIOS) {
            // // console.info('💡 iOS: Las notificaciones web no están disponibles en Safari');
            // En iOS, resolver como exitoso pero sin notificaciones
            resolve();
            return;
          }
          reject(new Error('Notification API not supported'));
          return;
        }

        const permissions: NotificationPermission = await Notification.requestPermission();
        if (permissions === 'granted') {
          // // // console.log('✅ Permisos de notificación concedidos');
          resolve();
        } else if (permissions === 'denied') {
          // // console.warn('❌ Permisos de notificación denegados');
          reject(new Error('Notification permission denied'));
        } else {
          // // console.warn('⏸️ Permisos de notificación pendientes');
          reject(new Error('Notification permission default'));
        }
      } catch (error) {
        // // console.error('🔥 Error solicitando permisos de notificación:', error);
        reject(error);
      }
    });
  }

  public async getCurrentPlatform(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      resolve(this.isIOS);
    });
  }


  public async getNotificationToken(): Promise<string> {
    try {
      // En iOS, Firebase messaging puede tener limitaciones
      if (this.isIOS) {
        // // console.warn('⚠️ iOS: Firebase messaging puede tener limitaciones');
        // Intentar de todas formas, pero con manejo de errores mejorado
      }

      // Verificar si service worker está disponible
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker no soportado');
      }

      const swRegistration = await navigator.serviceWorker.register('/fcm/firebase-messaging-sw.js', {
        // type: 'module',
        scope: '/fcm/'
      });

      // Esperar a que el service worker esté listo
      // await navigator.serviceWorker.ready;

      const token = await getToken(this.messaging, {
        serviceWorkerRegistration: swRegistration,
        vapidKey: environment.vapidkey
      });

      if (token) {
        // // // console.log('🎯 Token de notificación obtenido:', token.substring(0, 20) + '...');
        return token;
      } else {
        throw new Error('No se pudo obtener el token de notificación');
      }
    } catch (error) {
      // // console.error('🔥 Error registrando service worker o obteniendo token:', error);

      // En iOS, no fallar completamente
      if (this.isIOS) {
        // // console.info('💡 iOS: Continuando sin token de notificación');
        return '';
      }

      throw error;
    }
  }

  public getNotifications(): Observable<any> {
    const DATABASE_REFERENCE: DatabaseReference = ref(this.DATABASE, `${this.DATABASE_PATH}`);
    return objectVal(DATABASE_REFERENCE);
  }

  public updateNotificationStatus(id: string, uid: string): Promise<void> {
    return new Promise( async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.DATABASE, `${this.DATABASE_PATH}/${id}/recipient_uid/${uid}/`);
      update(DATABASE_REFERENCE, {
        status: 'readed'
      }).then(() => {
        resolve();
      }).catch((error: any) => {
        reject();
      })
    });
  }

  public sendPushNotifications(notification_data: any, tokens: string[]): Promise<void> {
    return new Promise( async (resolve, reject) => {
      try {
        // alert(1)
        // Crear un identificador único para la notificación
        const notificationId = this.createNotificationId(notification_data);
        // alert(2)

        // Verificar si ya se envió esta notificación recientemente
        if (this.isNotificationRecentlySent(notificationId)) {
          // alert(3)
          // // // console.log('🔔 Notificación ya enviada recientemente, omitiendo:', notificationId);
          resolve();
          return;
        }
        // alert(4)

        // Filtrar tokens válidos
        const validTokens = tokens.filter(token => token && token.length > 0);
        // alert('Tokens validos' + JSON.stringify(validTokens));
        // alert(5)

        if (validTokens.length === 0) {
          // alert(6)
          // // console.info('💡 No hay tokens válidos para push notifications');
          resolve();
          return;
        }
        // alert(7)
        // alert(7)

        // Marcar como enviada
        this.markNotificationAsSent(notificationId);
        // alert(8)

        const CURRENT_HOST: string = location.hostname;
        const NOTIFICATIONS_PAYLOAD: any = {
          title: notification_data.title,
          body: notification_data.body,
          tokens: validTokens,
          data: {
            notification_type: notification_data.is_chat == true ? 'chat_message' : 'new_request',
            recipient_uid: notification_data.recipient_uid,
            timestamp: Date.now(),
          }
        };
        notification_data.is_chat == true ? NOTIFICATIONS_PAYLOAD.data['chat_id'] = notification_data.chat_id : NOTIFICATIONS_PAYLOAD.data['request_id'] = notification_data.request_id;
        const PAYLOAD_AS_TEXT = JSON.stringify(NOTIFICATIONS_PAYLOAD);
        // alert(PAYLOAD_AS_TEXT)
        this.NEW_REQUEST_SUBSCRIPTION = this.httpClient.post(`${environment.cloud_functions_urls.sendPushNotifications}`, PAYLOAD_AS_TEXT).pipe(catchError((errorTest:HttpErrorResponse): any => {
          // alert(JSON.stringify(errorTest))
          if (errorTest.error instanceof ErrorEvent) {
            reject();
          } else {
            reject();
          }
        })).subscribe((response: any) => {}, (error: any) => { reject() }, () => { resolve() });
      } catch (error) {
        reject(error);
      }
    });
  }

  public sendMessageEmailNotifications(notification_data: any): Promise<void> {
    return new Promise( async (resolve, reject) => {
      try {
        const NOTIFICATIONS_PAYLOAD: any = {
          email: notification_data.email,
          sender_name: notification_data.sender_name,
          app: notification_data.app,
          message: notification_data.message,
          timestamp: notification_data.timestamp
        };
        const PAYLOAD_AS_TEXT = JSON.stringify(NOTIFICATIONS_PAYLOAD);
        this.NEW_REQUEST_SUBSCRIPTION = this.httpClient.post(`${environment.cloud_functions_urls.sendMessageNotificationEmail}`, PAYLOAD_AS_TEXT).pipe(catchError((errorTest:HttpErrorResponse): any => {
          if (errorTest.error instanceof ErrorEvent) {
            reject();
          } else {
            reject();
          }
        })).subscribe((response: any) => {}, (error: any) => { reject() }, () => { resolve() });
      } catch (error) {
        reject(error);
      }
    });
  }

  public removeNotifications(notifications: any): Promise<void> {
    return new Promise( async (resolve, reject) => {
      const DATABASE_REFERENCE: DatabaseReference = ref(this.DATABASE, this.DATABASE_PATH);
      update(DATABASE_REFERENCE, notifications).then(() => {
        resolve();
      }).catch((error: any) => {
        reject();
      })
    });
  }

  // Métodos helper para verificar capacidades
  public isIOSDevice(): boolean {
    return this.isIOS;
  }

  public isNotificationAPISupported(): boolean {
    return this.isNotificationSupported;
  }

  public canUseNotifications(): boolean {
    return this.isNotificationSupported && !this.isIOS;
  }

  public getNotificationCapabilities(): {
    isIOS: boolean;
    isSupported: boolean;
    canUse: boolean;
    message: string;
  } {
    return {
      isIOS: this.isIOS,
      isSupported: this.isNotificationSupported,
      canUse: this.canUseNotifications(),
      message: this.isIOS
        ? 'iOS Safari no soporta notificaciones web push'
        : this.isNotificationSupported
          ? 'Notificaciones disponibles'
          : 'Notificaciones no soportadas en este navegador'
    };
  }

  /**
   * Crea un identificador único para la notificación
   */
  private createNotificationId(notification_data: any): string {
    if (notification_data.is_chat) {
      return `chat_${notification_data.chat_id}_${notification_data.recipient_uid}_${Date.now()}`;
    } else {
      return `request_${notification_data.request_id}_${notification_data.recipient_uid}_${Date.now()}`;
    }
  }

  /**
   * Verifica si la notificación ya fue enviada recientemente
   */
  private isNotificationRecentlySent(notificationId: string): boolean {
    return this.sentNotifications.has(notificationId);
  }

  /**
   * Marca la notificación como enviada y programa su limpieza
   */
  private markNotificationAsSent(notificationId: string): void {
    this.sentNotifications.add(notificationId);

    // Limpiar después del TTL para evitar acumulación de memoria
    setTimeout(() => {
      this.sentNotifications.delete(notificationId);
    }, this.NOTIFICATION_TTL);
  }
}
